import { MIN_TARGET_KBPS, TARGET_KBPS, type CbrKbps } from './types'

// CBR bitrates the wasm-media-encoders LAME build accepts, ascending.
// Note: 56 kbps is valid MPEG-1 Layer III but the library rejects it.
const VALID_KBPS: readonly CbrKbps[] = [32, 40, 48, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320]

// Snap a target bitrate down to the nearest valid encoder value. Caps at
// `ceiling` (default 128) so we never increase quality above our default.
// Floors at MIN_TARGET_KBPS to avoid unusable output.
export function snapDownKbps(target: number, ceiling: number = TARGET_KBPS): CbrKbps {
  const limit = Math.min(target, ceiling)
  let result: CbrKbps = MIN_TARGET_KBPS
  for (const k of VALID_KBPS) {
    if (k < MIN_TARGET_KBPS) continue
    if (k <= limit) result = k
    else break
  }
  return result
}

// Returns a target kbps that should produce an encoded file <= maxOutputBytes
// for a source of (sourceSizeBytes, sourceKbps). Returns undefined when no
// override is needed (source already fits) or when we lack the info to
// compute one (caller should fall back to TARGET_KBPS).
export function computeTargetKbpsForSize(
  sourceSizeBytes: number,
  sourceKbps: number,
  maxOutputBytes: number,
  marginFactor = 0.95,
): CbrKbps | undefined {
  if (sourceSizeBytes <= maxOutputBytes) return undefined
  if (sourceKbps <= 0) return undefined
  const durationSec = (sourceSizeBytes * 8) / (sourceKbps * 1000)
  if (durationSec <= 0) return undefined
  const targetBitsPerSec = (maxOutputBytes * 8 * marginFactor) / durationSec
  const targetKbps = targetBitsPerSec / 1000
  return snapDownKbps(targetKbps)
}
